import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import endpoints from "../../api/endpoints";
import ProcessingBtn from "../../components/btn/ProcessingBtn";
import moment from "moment";
import { useTranslation } from "react-i18next";

export default function CreateCouponModal({
  isModalOpen,
  setIsModalOpen,
  refresh,
}) {
  function closeModal() {
    setIsModalOpen(false);
  }

  const { t } = useTranslation();
  const [warning, setWarning] = useState();
  const axiosPrivate = useAxiosPrivate();
  const controller = new AbortController();

  const [isLoading, setLoading] = useState(false);

  const submitHandler = async (values, { setErrors, resetForm }) => {
    setLoading(true);
    try {
      const res = await axiosPrivate.post(endpoints.COUPONS_URL, values, {
        signal: controller.signal,
      });

      // Refresh
      await refresh();

      // Toast
      toast.success(`Coupon "${res.data.code}" created successfully`);

      // Close the modal
      setIsModalOpen(!isModalOpen);
    } catch (err) {
      if (!err?.response) {
        toast.error("No Server res");
      } else {
        // API Error
        let error = err?.response?.data?.error.msg;
        toast.error(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      code: "",
      qty: 0,
      isUnlimited: true,
      type: "plan",
      plans: [],
      premiumDays: null,
      expiredAt: moment().add(7, "days"),
      hasExpiry: false,
    },
    validationSchema: Yup.object({
      code: Yup.string().trim().required(t("This field is required")),
      qty: Yup.number(t("Must be number"))
        .integer(t("Must be integer"))
        .min(0, t("Must be greater than 0")),
      isUnlimited: Yup.boolean().required(t("This field is required")),
      type: Yup.string()
        .oneOf(["plan", "premium"])
        .required(t("This field is required")),
      premiumDays: Yup.number()
        .nullable(true)
        .when("type", {
          is: "premium",
          then: Yup.number(t("Must be number"))
            .integer(t("Must be integer"))
            .min(1, t("Must be greater than 0"))
            .required(t("This field is required")),
        }),
      expiredAt: Yup.date().optional(),
      hasExpiry: Yup.boolean().required(t("This field is required")),
    }),
    onSubmit: submitHandler,
  });

  return (
    <>
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={closeModal}>
          {/* The backdrop, rendered as a fixed sibling to the panel container */}
          <div
            className="fixed inset-0 backdrop-blur-sm backdrop-brightness-75 transition-opacity"
            aria-hidden="true"
          />
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 dark:text-white p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6"
                  >
                    {t("Create Coupon")}
                  </Dialog.Title>
                  {/* Warning */}
                  {warning !== null && <div className="py-2">{warning}</div>}

                  {/* Form */}
                  <form onSubmit={formik.handleSubmit}>
                    <div className="space-y-4">
                      <div>
                        <label
                          className="block text-sm font-medium mb-1"
                          htmlFor="code"
                        >
                          {t("Code")}
                          <span className="text-rose-500">*</span>
                        </label>
                        <input
                          name="code"
                          className="form-input w-full text-lg sm:text-base dark:text-white dark:bg-gray-800 dark:border-gray-600"
                          type="string"
                          value={formik.values.code}
                          onChange={(e) => {
                            // Turn to upper case
                            e.target.value = e.target.value.toUpperCase();
                            formik.handleChange(e);
                          }}
                          onBlur={formik.handleBlur}
                        />
                        <p className="text-red-600 text-sm">
                          {formik.errors.code && formik.touched.code
                            ? `Error: ${formik.errors.code}`
                            : null}
                        </p>
                      </div>

                      <div>
                        <label
                          className="block text-sm font-medium mb-1"
                          htmlFor="title"
                        >
                          {t("Title")}
                        </label>
                        <input
                          name="title"
                          className="form-input w-full text-lg sm:text-base dark:text-white dark:bg-gray-800 dark:border-gray-600"
                          type="string"
                          value={formik.values.title}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        <p className="text-red-600 text-sm">
                          {formik.errors.title && formik.touched.title
                            ? `Error: ${formik.errors.title}`
                            : null}
                        </p>
                      </div>

                      <div>
                        <label
                          className="block text-sm font-medium mb-1"
                          htmlFor="description"
                        >
                          {t("Description")}
                        </label>
                        <textarea
                          name="description"
                          className="form-input w-full text-lg sm:text-base dark:text-white dark:bg-gray-800 dark:border-gray-600"
                          type="string"
                          value={formik.values.description}
                          onChange={formik.handleChange}
                          rows={4}
                          onBlur={formik.handleBlur}
                        />
                        <p className="text-red-600 text-sm">
                          {formik.errors.description &&
                          formik.touched.description
                            ? `Error: ${formik.errors.description}`
                            : null}
                        </p>
                      </div>

                      {formik.values.type === "premiium" && (
                        <div>
                          <label
                            className="block text-sm font-medium mb-1"
                            htmlFor="premiumDays"
                          >
                            {t("Premium Days")}
                          </label>
                          <input
                            name="premiumDays"
                            className="form-input w-full text-lg sm:text-base dark:text-white dark:bg-gray-800 dark:border-gray-600"
                            type="string"
                            value={formik.values.premiumDays}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <p className="text-red-600 text-sm">
                            {formik.errors.premiumDays &&
                            formik.touched.premiumDays
                              ? `Error: ${formik.errors.premiumDays}`
                              : null}
                          </p>
                        </div>
                      )}
                      <div>
                        <div className="flex items-center space-x-4">
                          <label
                            className="block text-sm font-medium mb-1"
                            htmlFor="hasExpiry"
                          >
                            {t("Will Expired")}
                          </label>
                          <div className="form-switch">
                            <input
                              name="hasExpiry"
                              type="checkbox"
                              id="hasExpiry"
                              className="sr-only"
                              checked={formik.values.hasExpiry}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                            <label className="bg-slate-400" htmlFor="hasExpiry">
                              <span
                                className="bg-white shadow-sm"
                                aria-hidden="true"
                              ></span>
                              <span className="sr-only">
                                {t("Will Expired")}
                              </span>
                            </label>
                          </div>
                        </div>
                        <p className="text-red-600 text-sm">
                          {formik.errors.hasExpiry && formik.touched.hasExpiry
                            ? `Error: ${formik.errors.hasExpiry}`
                            : null}
                        </p>
                      </div>
                      <div>
                        <div className="flex items-center space-x-4">
                          <label
                            className="block text-sm font-medium mb-1"
                            htmlFor="code"
                          >
                            {t("Unlimited Qty")}
                          </label>
                          <div className="form-switch">
                            <input
                              name="isUnlimited"
                              type="checkbox"
                              id="isUnlimited"
                              className="sr-only"
                              checked={formik.values.isUnlimited}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                            />
                            <label
                              className="bg-slate-400"
                              htmlFor="isUnlimited"
                            >
                              <span
                                className="bg-white shadow-sm dark:text-white dark:bg-gray-800 dark:border-gray-600"
                                aria-hidden="true"
                              ></span>
                              <span className="sr-only">
                                {t("Unlimited Quantity")}
                              </span>
                            </label>
                          </div>
                        </div>
                        <p className="text-red-600 text-sm">
                          {formik.errors.isUnlimited &&
                          formik.touched.isUnlimited
                            ? `Error: ${formik.errors.isUnlimited}`
                            : null}
                        </p>
                      </div>
                      {!formik.values.isUnlimited && (
                        <div>
                          <label
                            className="block text-sm font-medium mb-1"
                            htmlFor="qty"
                          >
                            {t("Qty")}
                          </label>
                          <input
                            name="qty"
                            className="form-input w-full text-lg sm:text-base dark:text-white dark:bg-gray-800 dark:border-gray-600"
                            type="number"
                            value={formik.values.qty}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <p className="text-red-600 text-sm">
                            {formik.errors.qty && formik.touched.qty
                              ? `Error: ${formik.errors.qty}`
                              : null}
                          </p>
                        </div>
                      )}
                      {formik.values.hasExpiry && (
                        <div>
                          <label
                            className="block text-sm font-medium mb-1"
                            htmlFor="expiredAt"
                          >
                            {t("Expiry Day")}
                          </label>
                          <input
                            name="expiredAt"
                            className="form-input w-full text-lg sm:text-base dark:text-white dark:bg-gray-800 dark:border-gray-600"
                            type="date"
                            value={formik.values.expiredAt}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                          <p className="text-red-600 text-sm">
                            {formik.errors.expiredAt && formik.touched.expiredAt
                              ? `Error: ${formik.errors.expiredAt}`
                              : null}
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between mt-6 space-x-4">
                      <button
                        type="button"
                        onClick={() => setIsModalOpen(!isModalOpen)}
                        className="btn bg-slate-500 hover:bg-slate-600 dark:bg-slate-600 dark:hover:bg-slate-700 text-white w-full"
                      >
                        {t("Close")}
                      </button>
                      {!isLoading && (
                        <button
                          type="submit"
                          className="btn bg-emerald-500 hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-700 text-white w-full"
                        >
                          {t("Create Coupon")}
                        </button>
                      )}
                      {isLoading && <ProcessingBtn title="Create Coupon" />}
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
